import { YoungsFilterSearchBar } from "@/components/filters/YoungsFilterSearchBar"
import { PageDialog } from "@/components/dialog/PageDialog"
import { QueryRender } from "@/components/helpers/QueryRender"
import { useUsers } from "@/entities/user/useUser"
import { useAttendanceEventsForAll, useDeleteAttendanceEvent } from "../../entities/attendanceEvent/useAttendanceEvent"
import { useGlobalSettings } from "@/entities/globalSettings/useGlobalSettings"
import {
   createColumnHelper,
   flexRender,
   getCoreRowModel,
   useReactTable,
} from "@tanstack/react-table"
import { isWithinInterval } from "date-fns"
import { useEffect, useMemo, useState } from "react"
import { AttendanceEventContent } from "../AttendanceEvent"
import { UserSmallProfile } from "@/components/ui/UserSmallProfile"
import { SchoolPeriodSelector } from "@/components/filters/SchoolPeriodSelector"
import { SchoolPeriod } from "@/entities/schoolYear/SchoolPeriod"
import { AttendanceEventFilterBar } from "@/components/filters/AttendanceEventFilterBar"
import { JustificationValidationStatus } from "@/entities/attendanceEvent/AttendanceEventType"
import { useAuth } from "@/hooks/useAuth"
import { createUsersById } from "@/utils/createUsersById"
import {
   formatDateRange,
   getStatusBadge,
   getTypeIcon,
   getTypeTitle,
} from "@/utils/commonAttendanceUi"
import { isSuperAdmin } from "@/config/env"
import { RecurrencePeriod, recurrencePeriodTranslations } from "@/types/recurrence"

export function sortByFrom<T extends { from: Date }>(items: T[] | undefined): T[] {
   if (!items) return []
   return items.sort((a, b) => a.from.getTime() - b.from.getTime())
}

export default function AbsencesEtRetards() {
   const { data: attendanceEvents, ...query } = useAttendanceEventsForAll({})
   const { data: users } = useUsers()
   const globalSettingsQuery = useGlobalSettings()
   const { user } = useAuth()
   const [selectedEventId, setSelectedEventId] = useState<string | null>(null)
   const [searchTerm, setSearchTerm] = useState("")
   const [selectedGroups, setSelectedGroups] = useState<string[]>([])
   const [selectedPeriod, setSelectedPeriod] = useState<SchoolPeriod | null>(null)
   const [selectedStatus, setSelectedStatus] = useState<JustificationValidationStatus | "all">(
      "admin_pending",
   )
   const deleteAttendanceEvent = useDeleteAttendanceEvent()

   useEffect(() => {
      if (globalSettingsQuery.data) {
         setSelectedGroups(globalSettingsQuery.data.groups)
      }
   }, [globalSettingsQuery.data?.groups])

   const usersById = useMemo(() => createUsersById(users), [users])

   const filteredEvents = useMemo(() => {
      const events = sortByFrom(attendanceEvents) || []
      return events.filter((event) => {
         if (
            selectedPeriod &&
            !isWithinInterval(event.from, {
               start: selectedPeriod!.start_date,
               end: selectedPeriod!.end_date,
            })
         )
            return false

         if (selectedStatus !== "all" && event.justification_validation_status !== selectedStatus)
            return false

         const user = usersById.get(event.young_id)
         if (!user) return false

         if (!selectedGroups.includes(user.group)) return false

         if (!searchTerm) return true
         const fullName = `${user.firstName} ${user.lastName}`.toLowerCase()
         return fullName.includes(searchTerm.toLowerCase())
      })
   }, [attendanceEvents, usersById, selectedGroups, searchTerm, selectedPeriod, selectedStatus])

   const columnHelper = createColumnHelper<any>()
   const columns = [
      columnHelper.accessor("type", {
         header: "Type",
         cell: (info) => (
            <span className={`flex items-center gap-2`}>
               {getTypeIcon(info.getValue())}
               {getTypeTitle(info.getValue())}
            </span>
         ),
      }),
      columnHelper.accessor("young_id", {
         header: "Jeune",
         cell: (info) => {
            const user = usersById.get(info.getValue())
            return user ? <UserSmallProfile user={user} /> : info.getValue()
         },
      }),
      columnHelper.accessor(
         (row) => ({ justified: row.justified, status: row.justification_validation_status }),
         {
            id: "status",
            header: "Statut",
            cell: (info) => {
               const { justified, status } = info.getValue()
               return getStatusBadge(justified, status)
            },
         },
      ),
      columnHelper.accessor(
         (row) => ({ from: row.from, to: row.to, type: row.type, delayMinutes: row.delayMinutes, is_recurring: row.is_recurring, recurring_period: row.recurring_period }),
         {
            id: "period",
            header: "Période",
            cell: (info) => {
               const { from, to, type, delayMinutes, is_recurring, recurring_period } = info.getValue()
               const suffix = type === "late" ? ` (${delayMinutes} minutes)` : ""
               const date = `${formatDateRange(from, to, type)} ${suffix}`
               if (is_recurring) {
                  const rec = recurrencePeriodTranslations[(recurring_period as RecurrencePeriod)]
                  return `${date} (chaque ${rec})`
               }
               return date
            },
         },
      ),
   ]

   const table = useReactTable({
      data: filteredEvents,
      columns,
      getCoreRowModel: getCoreRowModel(),
   })

   return (
      <div className="page-section">
         <h1 className="title">Absences et retards</h1>

         <div className="mb-6">
            <SchoolPeriodSelector setSelectedSchoolPeriod={setSelectedPeriod} />
         </div>

         <div className="mb-6">
            <YoungsFilterSearchBar
               searchText={searchTerm}
               setSearchText={setSearchTerm}
               selectedGroups={selectedGroups}
               setSelectedGroups={setSelectedGroups}
            />
         </div>

         <div className="mb-6">
            <AttendanceEventFilterBar
               selectedStatus={selectedStatus}
               setSelectedStatus={setSelectedStatus}
            />
         </div>

         <QueryRender query={query} data={filteredEvents}>
            {(filteredEvents) => (
               <>
                  <div className="">
                     <div className="hidden md:block overflow-x-auto">
                        <table className="table">
                           <thead>
                              {table.getHeaderGroups().map((headerGroup) => (
                                 <tr key={headerGroup.id}>
                                    {headerGroup.headers.map((header) => (
                                       <th key={header.id}>
                                          {header.isPlaceholder
                                             ? null
                                             : flexRender(
                                                  header.column.columnDef.header,
                                                  header.getContext(),
                                               )}
                                       </th>
                                    ))}
                                 </tr>
                              ))}
                           </thead>
                           <tbody>
                              {table.getRowModel().rows.map((row) => (
                                 <tr
                                    key={row.id}
                                    className="hover cursor-pointer"
                                    onClick={() => setSelectedEventId(row.original.id)}
                                 >
                                    {row.getVisibleCells().map((cell) => (
                                       <td key={cell.id}>
                                          {flexRender(
                                             cell.column.columnDef.cell,
                                             cell.getContext(),
                                          )}
                                       </td>
                                    ))}
                                    {isSuperAdmin && (
                                       <td>
                                          <button className="btn btn-error" onClick={(e) => {
                                             e.stopPropagation()
                                             deleteAttendanceEvent(row.original.id)
                                          }}>Remove</button>
                                       </td>
                                    )}
                                 </tr>
                              ))}
                           </tbody>
                        </table>
                     </div>

                     <div className="md:hidden grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {filteredEvents.map((event) => {
                           return (
                              <div
                                 key={event.id}
                                 className="card bg-base-100 shadow-md hover:shadow-lg cursor-pointer transition-shadow"
                                 onClick={() => setSelectedEventId(event.id)}
                              >
                                 <div className="card-body p-4 md:p-8">
                                    <AttendanceEventContent
                                       id={event.id}
                                       user={user!}
                                       variant="small"
                                    />
                                 </div>
                              </div>
                           )
                        })}
                     </div>
                  </div>
                  <PageDialog isOpen={!!selectedEventId} onClose={() => setSelectedEventId(null)}>
                     {selectedEventId && (
                        <AttendanceEventContent id={selectedEventId} user={user!} variant="full" onDeleted={() => setSelectedEventId(null)} />
                     )}
                  </PageDialog>
               </>
            )}
         </QueryRender>
      </div>
   )
}
